import React from "react";
import {
  Box,
  Typography,
  Chip,
  Collapse,
} from "@mui/material";
import { TimestampDisplay } from "../../internal/components/TimestampDisplay";
import { ActionLogFullErrorDisplay } from "./ActionLogErrorIndicator";
import { ActionLogActionButtons } from "./ActionLogActionButtons";
import { ActionLogInfo } from "@services/admin/types";

interface ActionLogExpandedContentProps {
  logs: ActionLogInfo[];
  isExpanded: boolean;
  includeDescription: boolean;
  displayErrors: boolean;
  onViewDetails: (log: ActionLogInfo) => void;
  onViewGptRequest: (gptRequestId: string) => void;
  onViewExtractedData: (extractedDataId: string) => void;
  onViewDerivedData: (derivedDataId: string) => void;
  onViewAIAgentTrace: (log: ActionLogInfo) => void;
  onViewErrors: (log: ActionLogInfo) => void;
}

const getTypeColor = (type: string) => {
  switch (type) {
    case "EmailSend":
      return "primary";
    case "GptRequest":
      return "secondary";
    case "CreateShipment":
    case "UpdateShipment":
      return "success";
    case "StartAIAgent":
      return "warning";
    case "EndAIAgent":
      return "success";
    default:
      return "default";
  }
};

export function ActionLogExpandedContent({
  logs,
  isExpanded,
  includeDescription,
  displayErrors,
  onViewDetails,
  onViewGptRequest,
  onViewExtractedData,
  onViewDerivedData,
  onViewAIAgentTrace,
  onViewErrors,
}: ActionLogExpandedContentProps) {
  return (
    <Collapse in={isExpanded}>
      <Box sx={{ ml: 4, mr: 2, my: 1 }}>
        {logs.map((log, index) => (
          <Box
            key={log.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1,
              px: 2,
              bgcolor:
                index % 2 === 0
                  ? "grey.25"
                  : "transparent",
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Chip
                label={log.type}
                size="small"
                color={getTypeColor(log.type) as any}
              />
              <TimestampDisplay
                timestamp={log.createdAt}
                variant="caption"
              />
              <Box>
                {log.memo && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {log.memo}
                  </Typography>
                )}
                {includeDescription &&
                  log.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontStyle: "italic",
                        whiteSpace: "pre-line",
                        mt: 0.5,
                      }}
                    >
                      {log.description}
                    </Typography>
                  )}
                {displayErrors &&
                  (log.extra?.errors ||
                    log.extra?.error ||
                    log.extra?.warnings ||
                    log.extra?.warning ||
                    log.extra?.infos ||
                    log.extra?.info) && (
                    <ActionLogFullErrorDisplay
                      log={log}
                    />
                  )}
              </Box>
            </Box>
            <ActionLogActionButtons
              log={log}
              onViewDetails={onViewDetails}
              onViewGptRequest={onViewGptRequest}
              onViewExtractedData={onViewExtractedData}
              onViewDerivedData={onViewDerivedData}
              onViewAIAgentTrace={onViewAIAgentTrace}
              onViewErrors={onViewErrors}
            />
          </Box>
        ))}
      </Box>
    </Collapse>
  );
}